import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { type Task } from "@/types/task";

export type TasksResponse = {
  tasks: Task[];
  should_poll_for_identity_verification: boolean;
  identity_verification_poll_attempts?: number;
};

const fetchTasks = async (): Promise<TasksResponse> => {
  const response = await fetch("/tasks.json");
  return response.json();
};

export default function useTasks() {
  const pollInterval = 3000;
  const [shouldPoll, setShouldPoll] = useState(false);

  const query = useSuspenseQuery<TasksResponse>({
    queryKey: ["tasks"],
    queryFn: fetchTasks,
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: shouldPoll ? pollInterval : false,
    refetchIntervalInBackground: false, // Stop polling when tab loses focus
  });

  // Polling is controlled by the server:
  // - true while session contains an identity verification id and attempts < MAX
  // - false once the task is found (and modal can auto-open) or attempts are exhausted
  useEffect(() => {
    const data = query.data;
    if (!data) return;

    setShouldPoll(data.should_poll_for_identity_verification);
  }, [query.data, shouldPoll]);

  return {
    ...query,
    data: query.data.tasks,
  };
}
